import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Input } from "@shared/ui/input";
import {
  Users,
  MapPin,
  Calendar,
  User,
  Zap,
  Search,
  Loader2,
  CheckCircle,
  Star,
} from "lucide-react";
import {
  useJobCardAssignment,
  useFieldForceRecommend,
} from "../../../../../hooks";
import {
  BulkAssignDialogProps,
  RecommendedTechnician,
  TransformedServiceOrder,
} from "@features/wx/dispatcher/types";
import { logEvent } from "@shared/analytics/analytics";

// Helper function to extract coordinates from service orders
const extractMeterLocations = (selectedOrdersData: any[]): string[] => {
  if (!selectedOrdersData || selectedOrdersData.length === 0) return [];

  const coordinates = [];

  for (const order of selectedOrdersData) {
    // Only include meter locations if association is "Meter"
    if (order.serviceAssociation !== "Meter") continue;

    // Check various possible coordinate fields
    if (order.latitude && order.longitude) {
      coordinates.push(`(${order.latitude}, ${order.longitude})`);
    } else if (order.location?.lat && order.location?.lng) {
      coordinates.push(`(${order.location.lat}, ${order.location.lng})`);
    } else if (order.meter?.coordinates) {
      const { lat, lng } = order.meter.coordinates;
      if (lat && lng) {
        coordinates.push(`(${lat}, ${lng})`);
      }
    } else if (order.coordinates) {
      coordinates.push(order.coordinates);
    }
  }

  return coordinates;
};

// Transform API data to match existing UI expectations
const transformTechnicianData = (technicians: RecommendedTechnician[]) => {
  return technicians.map((tech) => ({
    id: tech.fieldForceId,
    name: tech.name,
    workload: tech.workLoad || 0,
    distance: tech.distance,
    skills: tech.skill || [],
    skillMatch: tech.skillMatch,
    status:
      tech.availabilityScore >= 100
        ? "available"
        : tech.workLoad >= 80
        ? "overloaded"
        : "busy",
    utilization: tech.workLoad || 0,
    rating: tech.rating || 0,
    bestMatch: tech.matchPercentage || 0,
    tagline: tech.recommendedReason || `${tech.roleName} specialist`,
    fieldForceNo: tech.fieldForceNo,
    contactNumber: tech.contactNumber,
    email: tech.email,
    originalData: tech,
  }));
};

export const TechnicianAssignmentModal: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedOrders: string[];
  selectedOrdersData: any[];
  sop: TransformedServiceOrder[];
 onComplete?: () => void;}> = ({ open, onOpenChange, selectedOrders, selectedOrdersData, sop,onComplete }) => {
  // Server-side search state management
  const [searchData, setSearchData] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [selectedTechnician, setSelectedTechnician] = useState<any>(null);

  // Extract meter locations and SOP ID from selected orders
  const meterLocations = useMemo(() => {
    return extractMeterLocations(selectedOrdersData);
  }, [selectedOrdersData]);

  const sopId = selectedOrdersData[0]?.id || "";

  const assignmentMutation = useJobCardAssignment();

  // Debounced search function
  const debouncedSearch = useCallback(() => {
    let timeoutId: NodeJS.Timeout;
    return (searchTerm: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log(
          "🔍 Bulk assignment debounced search triggered:",
          searchTerm
        );
        setSearchData(searchTerm);
      }, 500);
    };
  }, []);

  const debouncedSearchFn = useMemo(() => debouncedSearch(), [debouncedSearch]);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (open) {
      setSearchData("");
      setSearchInputValue("");
      setSelectedTechnician(null);
    }
  }, [open]);

  // API call for field force recommendations with server-side search
  const {
    data: recommendationData,
    isLoading: loadingTechnicians,
    error: technicianError,
    refetch: refetchTechnicians,
  } = useFieldForceRecommend(
    {
      sopId: sop != null ? sop[0]?.sop.toString() : "",
      meterLocations: meterLocations,
      searchData: searchData || "", // Add search parameter to API call
    },
    {
      enabled: !!sopId && open && selectedOrdersData.length > 0,
    }
  );
  console.log("Recommendation Data:", recommendationData);

  // Transform API data or use empty array - NO CLIENT-SIDE FILTERING
  const allTechnicians = useMemo(() => {
    if (!recommendationData?.results) return [];
    console.log('recommendationData',recommendationData.results)

    const transformed = transformTechnicianData(recommendationData.results);

    // Sort by recommendation score, then by workload
    return transformed.sort((a, b) => {
      if (a.bestMatch !== b.bestMatch) {
        return b.bestMatch - a.bestMatch;
      }
      return a.workload - b.workload;
    });
  }, [recommendationData]);

  const getWorkloadColor = (workload: number) => {
    if (workload === 0) return "text-green-600";
    if (workload >= 6) return "text-red-600";
    return "text-orange-600";
  };

  const getRecommendationColor = (score: number) => {
    if (score >= 90) return "text-green-600";
    if (score >= 70) return "text-blue-600";
    return "text-yellow-600";
  };

  const handleAssign = async () => {
    if (!selectedTechnician) return;

    try {
      // Convert string IDs to numbers
      const jobCardIds = selectedOrders.map((id) => parseInt(id));

      await assignmentMutation.mutateAsync({
        job_card_ids: jobCardIds,
        field_force_id: selectedTechnician.id,
      }, {
        onSuccess: () => {
          logEvent('Bulk Assignment SOP Used', {
            orderCount: selectedOrders.length,
          })
        }
      });

      onOpenChange(false);
      setSelectedTechnician(null);

       // 👇 Close everything and clear all state
    if (onComplete) {
      onComplete();
    }
    } catch (error) {}
  };

  const handleAutoAssign = () => {
    if (allTechnicians.length === 0) return;

    // Find best technician based on recommendation score and availability
    const availableTechnicians = allTechnicians.filter(
      (tech) => tech.status === "available"
    );

    const bestTechnician =
      availableTechnicians.length > 0
        ? availableTechnicians[0] // Already sorted by recommendation score
        : allTechnicians[0]; // Fallback to first technician

    setSelectedTechnician(bestTechnician);
  };

  const handleRetry = () => {
    refetchTechnicians();
  };

  const totalServices = selectedOrdersData.reduce(
    (sum, order) => sum + (order.serviceCount || 1),
    0
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader className="border-b pb-4 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">
            Assign Technician - {selectedOrders.length} Service Orders
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left Panel - Summary */}
          <div className="w-1/3 border-r p-4 space-y-4 flex-shrink-0">
            {/* Summary Cards */}
            <div className="space-y-3">
              <h3 className="font-medium">Assignment Summary</h3>
              <div className="grid grid-cols-1 gap-3">
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Service Orders</p>
                      <p className="text-lg font-bold">
                        {selectedOrders.length}
                      </p>
                    </div>
                    <User className="h-5 w-5 text-blue-500" />
                  </div>
                </div>

                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Services</p>
                      <p className="text-lg font-bold">{totalServices}</p>
                    </div>
                    <Zap className="h-5 w-5 text-green-500" />
                  </div>
                </div>
              </div>
            </div>

            {/* Expected Date */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-gray-600" />
                <h3 className="font-medium">Expected Date</h3>
              </div>
              <p className="text-sm">December 15, 2024</p>
              <p className="text-xs text-gray-600">3 days from now</p>
            </div>

            {/* Service Types */}
            <div className="border rounded-lg p-3">
              <h3 className="font-medium mb-2">Service Types</h3>
              <div className="flex flex-wrap gap-1">
                {[
                  ...new Set(
                    selectedOrdersData.map((order) => order.serviceAssociation)
                  ),
                ].map((type) => (
                  <Badge
                    key={type}
                    className="bg-blue-100 text-blue-800 text-xs"
                  >
                    {type}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Areas */}
            <div className="border rounded-lg p-3">
              <h3 className="font-medium mb-2">Areas</h3>
              <div className="space-y-1">
                {[...new Set(selectedOrdersData.map((order) => order.area))]
                  .slice(0, 3)
                  .map((area) => (
                    <div key={area} className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-gray-500" />
                      <span className="text-xs text-gray-600">{area}</span>
                    </div>
                  ))}
                {[...new Set(selectedOrdersData.map((order) => order.area))]
                  .length > 3 && (
                  <p className="text-xs text-gray-500">
                    +
                    {[...new Set(selectedOrdersData.map((order) => order.area))]
                      .length - 3}{" "}
                    more areas
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Technicians */}
          <div className="flex-1 flex flex-col min-h-0 p-4">
            {/* Server-side Search and Auto-assign */}
            <div className="flex gap-3 mb-4 flex-shrink-0">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name, ID, or skills..."
                  value={searchInputValue}
                  onChange={(e) => {
                    setSearchInputValue(e.target.value);
                    debouncedSearchFn(e.target.value);
                  }}
                  className="pl-10"
                  disabled={loadingTechnicians}
                />
                {loadingTechnicians && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                )}
              </div>
              {allTechnicians.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleAutoAssign}
                  className="bg-orange-500 text-white hover:bg-orange-600 whitespace-nowrap"
                  disabled={loadingTechnicians}
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Auto-Assign
                </Button>
              )}
            </div>

            {/* Search Results Header */}
            {searchData && (
              <div className="text-xs text-gray-500 mb-3 px-1">
                Found {allTechnicians.length} technician(s) matching "
                {searchData}"
              </div>
            )}

            {/* Technicians List */}
            <div className="flex-1 overflow-y-auto">
              {technicianError ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center text-red-600">
                    <Users className="h-8 w-8 mx-auto mb-2 text-red-300" />
                    <p className="text-sm mb-2">Failed to load technicians</p>
                    <Button variant="outline" size="sm" onClick={handleRetry}>
                      Try Again
                    </Button>
                  </div>
                </div>
              ) : allTechnicians.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">
                      {searchData
                        ? `No technicians found matching "${searchData}"`
                        : "No technicians available"}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {allTechnicians.map((technician: any) => (
                    <div
                      key={technician.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedTechnician?.id === technician.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedTechnician(technician)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                            {technician.name
                              .split(" ")
                              .map((n: string) => n[0])
                              .join("")}
                          </div>
                          <div>
                            <p className="font-medium">{technician.name}</p>
                            <p className="text-sm text-gray-500">
                              {technician.code}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {technician.bestMatch > 0 && (
                            <div className="flex items-center gap-1">
                              <Star className="h-3 w-3 text-yellow-500" />
                              <span
                                className={`text-xs font-medium ${getRecommendationColor(
                                  technician.bestMatch
                                )}`}
                              >
                                {technician.bestMatch}% Match
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-gray-500" />
                            <span className="text-xs text-gray-500">
                              {technician.distance}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-gray-500" />
                          <span className="text-xs text-gray-500">
                            Workload:{" "}
                          </span>
                          <span
                            className={`font-medium text-xs ${getWorkloadColor(
                              technician.workload
                            )}`}
                          >
                            {technician.workload}
                          </span>
                        </div>
                        {technician.bestMatch > 0 && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            AI Recommended
                          </Badge>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-1">
                        {technician.skills.slice(0, 3).map((skill: string) => (
                          <Badge
                            key={skill}
                            variant="outline"
                            className="text-xs px-2 py-0"
                          >
                            {skill}
                          </Badge>
                        ))}
                        {technician.skills.length > 3 && (
                          <Badge
                            variant="outline"
                            className="text-xs px-2 py-0"
                          >
                            +{technician.skills.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4 flex-shrink-0">
          <div className="flex gap-3 w-full justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={!selectedTechnician || assignmentMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {assignmentMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <User className="h-4 w-4 mr-2" />
                  Assign {selectedOrders.length} Orders
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
